// How an alert reads and where it leads, shared by the dashboard's «Требует внимания», the «Мои задачи» card
// and the header bell (P6-E02). The copy lives in the dashboard namespace (`alerts.kind.*`, `alerts.pill.*`).
import { paths, type SiteTab } from '@/app/paths';
import type { IconName, Tone } from '@/design-system';

type Tv = (k: string, o?: Record<string, unknown>) => string;

/** The alert kinds the API sends (P5-E05, P6-E01, P7-A03): their icon and pill tone. */
export const ALERT: Record<string, { icon: IconName; tone: Tone }> = {
  build_failed: { icon: 'hammer', tone: 'err' },
  dns_unconfirmed: { icon: 'globe-lock', tone: 'warn' },
  dns_unverified: { icon: 'globe-lock', tone: 'warn' },
  domain_failed: { icon: 'globe-lock', tone: 'err' },
  research_partial: { icon: 'scan-search', tone: 'warn' },
  subscription_expiring: { icon: 'calendar-clock', tone: 'acc' },
  payment_overdue: { icon: 'wallet-cards', tone: 'err' },
  job_dead: { icon: 'triangle-alert', tone: 'err' },
  integration_error: { icon: 'plug-zap', tone: 'err' },
  ai_budget_warning: { icon: 'sparkles', tone: 'warn' },
  ai_budget_reached: { icon: 'sparkles', tone: 'err' },
  domain_expiring: { icon: 'globe', tone: 'acc' },
  tls_expiring: { icon: 'shield-alert', tone: 'err' },
};

const SOURCE_ICON: Record<string, IconName> = { website: 'globe', instagram: 'instagram', telegram: 'send', file: 'file-spreadsheet' };

/** The website tab each alert about a website opens. */
const SITE_TAB: Record<string, SiteTab> = {
  build_failed: 'variants',
  dns_unconfirmed: 'domains',
  dns_unverified: 'domains',
  domain_failed: 'domains',
  domain_expiring: 'domains',
  tls_expiring: 'deploy',
};

/** The icon of a finished job's notification (P6-E01 `<job type>.<succeeded|failed>`). */
const JOB_ICON: Record<string, IconName> = {
  'publish.deploy': 'rocket',
  'publish.rollback': 'undo-2',
  'store.provision': 'shopping-bag',
  'store.catalog_import': 'package',
  'store.theme_sync': 'palette',
  'integrations.connect': 'plug-zap',
  'domain.verify': 'shield-check',
  'domain.purchase': 'globe',
  'research.run': 'scan-search',
  'ai.generate_variant': 'sparkles',
};

/** The website tab a finished job opens. */
const JOB_TAB: Record<string, SiteTab> = {
  'publish.deploy': 'deploy',
  'publish.rollback': 'deploy',
  'store.provision': 'sync',
  'store.catalog_import': 'sync',
  'store.theme_sync': 'sync',
  'domain.verify': 'domains',
  'domain.purchase': 'domains',
  'ai.generate_variant': 'variants',
};

/** «publish.deploy.failed» → {type: «publish.deploy», ok: false}; null for an alert kind. */
export function jobKind(kind: string): { type: string; ok: boolean } | null {
  const m = /^(.+)\.(succeeded|failed)$/.exec(kind);
  return m ? { type: m[1] as string, ok: m[2] === 'succeeded' } : null;
}

export function alertMeta(kind: string, target: Record<string, unknown>, severity?: string) {
  const job = jobKind(kind);
  if (job) return { icon: JOB_ICON[job.type] ?? ('circle-check' as IconName), tone: (job.ok ? 'ok' : 'err') as Tone, source: '' };
  const meta = ALERT[kind] ?? { icon: 'triangle-alert' as IconName, tone: (severity as Tone) ?? 'mute' };
  const source = typeof target.source === 'string' ? target.source : '';
  const icon = kind === 'research_partial' && SOURCE_ICON[source] ? (SOURCE_ICON[source] as IconName) : meta.icon;
  return { icon, tone: meta.tone, source };
}

/** The alert's headline in the UI's language (the API's `title` when the kind is unknown). */
export function alertTitle(kind: string, title: string, target: Record<string, unknown>, tv: Tv): string {
  const job = jobKind(kind);
  if (job) return tv(`notifications.job.${job.ok ? 'ok' : 'failed'}`, { job: tv(`notifications.jobs.${job.type.replace(/\./g, '_')}`, { defaultValue: job.type }) });
  const source = typeof target.source === 'string' ? target.source : '';
  if (kind === 'subscription_expiring' || kind === 'domain_expiring' || kind === 'tls_expiring') return tv(`alerts.kind.${kind}`, { count: Number(target.days ?? 0) });
  // The API names the provider by its key («icangroup»): the card's name is the integrations namespace's.
  if (kind === 'integration_error') {
    const provider = typeof target.provider === 'string' ? target.provider : '';
    return tv(`alerts.kind.${kind}`, { provider: tv(`integrations:providers.${provider}.name`, { defaultValue: provider }), defaultValue: title });
  }
  return tv(`alerts.kind.${kind}`, { ...target, source: tv(`alerts.source.${source}`, { defaultValue: source }), defaultValue: title });
}

/** The screen an alert (or a finished job) opens: the website's tab, the organization's tab, or Тарифы; null when it names none. */
export function alertPath(kind: string, target: Record<string, unknown>): string | null {
  const job = jobKind(kind);
  if (job) {
    const id = typeof target.uuid === 'string' ? target.uuid : null;
    if (!id) return null;
    if (target.entity === 'website') return paths.site(id, JOB_TAB[job.type] ?? 'overview');
    if (target.entity === 'organization') return paths.organization(id, job.type === 'research.run' ? 'research' : job.type.startsWith('integrations.') ? 'integrations' : 'overview');
    return null;
  }
  const website = typeof target.website === 'string' ? target.website : null;
  const organization = typeof target.organization === 'string' ? target.organization : null;
  if (website) return paths.site(website, SITE_TAB[kind] ?? 'overview');
  if (kind === 'subscription_expiring') return paths.plans();
  if (organization) {
    if (kind === 'research_partial') return paths.organization(organization, 'research');
    if (kind === 'integration_error') return paths.organization(organization, 'integrations');
    if (kind === 'payment_overdue' || kind.startsWith('ai_budget')) return paths.organization(organization, 'subscription');
    return paths.organization(organization, 'overview');
  }
  return null;
}
